import { GetTasksApiV1TasksGetQueryParams } from "../../../../vl-api/vlComponents";
import {
  TaskStatus,
  TaskType,
  TaskFilterOptionsResponse,
} from "../../../../vl-api/vlSchemas";
import React, { useEffect, useMemo } from "react";
import { useNavigate, NavigateFunction } from "react-router-dom";
import { getTextByStatus, getTaskNameByTaskType } from "./taskManagerUtils";
import { AvailableFilter } from "views/uikit/VLTable";
import { isOnPremUser } from "helpers/utility/utilities";
import { useFeatureFlagService } from "hooks/useFeatureFlagService";
import { USER_MANAGEMENT } from "helpers/constants/filters";
import moment from "moment";
import { PAGE_PARAMETER } from "helpers/constants/miscellaneous";

export const createStatusFilterOptions = () => {
  const allStatuses = Object.keys(getTextByStatus) as TaskStatus[];

  return allStatuses.map((status) => ({
    text: getTextByStatus[status]?.() || status,
    value: status,
  }));
};

const createFilterOptions = (items: string[] = []) =>
  items.map((item) => ({
    text: item,
    value: item,
  }));

export enum OperationTimePeriod {
  LAST_24_HOURS = "last_24_hours",
  LAST_7_DAYS = "last_7_days",
  LAST_14_DAYS = "last_14_days",
}

export const OPERATION_TIME_FILTER_OPTIONS = [
  { text: "Last 24 Hours", value: OperationTimePeriod.LAST_24_HOURS },
  { text: "Last 7 Days", value: OperationTimePeriod.LAST_7_DAYS },
  { text: "Last 14 Days", value: OperationTimePeriod.LAST_14_DAYS },
];

export const convertTimePeriodToDateRange = (
  periods: OperationTimePeriod[],
): { start_time_from?: string; start_time_to?: string } => {
  if (!periods || periods.length === 0) {
    return {};
  }

  const now = new Date();
  let earliestDate: Date | null = null;

  // Find the earliest date from all selected periods
  for (const period of periods) {
    let periodStartDate: Date | null = null;

    switch (period) {
      case OperationTimePeriod.LAST_24_HOURS:
        periodStartDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case OperationTimePeriod.LAST_7_DAYS:
        periodStartDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case OperationTimePeriod.LAST_14_DAYS:
        periodStartDate = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
        break;
      default:
        continue;
    }
    // Use the earliest date from all selected periods
    if (periodStartDate && (!earliestDate || periodStartDate < earliestDate)) {
      earliestDate = periodStartDate;
    }
  }

  if (!earliestDate) {
    return {};
  }

  return {
    start_time_from: moment(earliestDate).format("YYYY-MM-DDTHH:mm:ss"),
    start_time_to: moment(now).format("YYYY-MM-DDTHH:mm:ss"),
  };
};

// Helper function to build URLSearchParams from filters
const buildURLSearchParams = (
  filters: Record<string, any>,
  resetPage: boolean,
): URLSearchParams => {
  const excludeParams = ["start_time_from", "start_time_to"];
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, values]) => {
    if (key === PAGE_PARAMETER && resetPage) {
      params.set(PAGE_PARAMETER, "1");
      return;
    }
    // Skip excluded params
    if (excludeParams.includes(key)) {
      return;
    }
    if (values !== undefined && values !== null && values !== "") {
      if (Array.isArray(values) && values.length > 0) {
        values.forEach((value) => {
          params.append(key, value);
        });
      } else if (typeof values === "string") {
        params.set(key, values);
      }
    }
  });

  return params;
};

export const parseFiltersFromURL = (
  search: string,
): Record<string, string[] | string> => {
  const params = new URLSearchParams(search);
  const filters: Record<string, string[] | string> = {};
  const paramKeys = Array.from(new Set(params.keys()));
  const singleValueParams = ["sort_by", "sort_order"];

  paramKeys.forEach((key) => {
    const values = params.getAll(key).filter(Boolean);
    if (values.length > 0) {
      // Handle single-value parameters like sort_by and sort_order
      if (singleValueParams.includes(key)) {
        filters[key] = values[0];
      } else {
        filters[key] = values;
      }
    }
  });
  return filters;
};

export const updateURLWithFilters = (
  navigate: NavigateFunction,
  filters: GetTasksApiV1TasksGetQueryParams,
  resetPage: boolean = true,
) => {
  const params = buildURLSearchParams(filters, resetPage);
  const search = params.toString();
  navigate(`/task-manager${search ? `?${search}` : ""}`, { replace: true });
};

export default function useTaskFilters(
  locationSearch: string,
  filterOptions?: TaskFilterOptionsResponse,
) {
  const [filters, setFilters] = React.useState<Record<string, any>>({});
  const navigate = useNavigate();
  const { show: isUserManagementEnabled } =
    useFeatureFlagService(USER_MANAGEMENT);

  useEffect(() => {
    const parsedFilters = parseFiltersFromURL(locationSearch);
    let needsURLUpdate = false;

    // Set default operationTime filter if not present
    if (!parsedFilters.operationTime) {
      parsedFilters.operationTime = [OperationTimePeriod.LAST_14_DAYS];
      needsURLUpdate = true;
    }

    // Set default sort if not present
    if (!parsedFilters.sort_by) {
      parsedFilters.sort_by = "start_time";
      needsURLUpdate = true;
    }
    if (!parsedFilters.sort_order) {
      parsedFilters.sort_order = "desc";
      needsURLUpdate = true;
    }

    if (needsURLUpdate) {
      // Update URL to include default filters (excluding time range params)
      const params = buildURLSearchParams(parsedFilters, false);
      const search = params.toString();
      navigate(`/task-manager${search ? `?${search}` : ""}`, { replace: true });
    }

    setFilters(parsedFilters);
  }, [locationSearch, navigate]);

  const availableFilters: AvailableFilter[] = useMemo(() => {
    const baseFilters: AvailableFilter[] = [
      {
        key: "operationTime",
        title: "Time",
        options: OPERATION_TIME_FILTER_OPTIONS.map((option) => ({
          text: option.text,
          value: option.value,
        })),
        searchable: false,
        type: "single",
      },
      {
        key: "task_type",
        title: "Task",
        options: (filterOptions?.task_types || []).map((taskType: string) => ({
          text: getTaskNameByTaskType[taskType as TaskType] || taskType,
          value: taskType,
        })),
        searchable: true,
      },
      {
        key: "dataset_name",
        title: "Dataset",
        options: createFilterOptions(filterOptions?.dataset_names),
        searchable: true,
      },
      {
        key: "dataset_id",
        title: "Dataset ID",
        options: createFilterOptions(filterOptions?.dataset_ids),
        searchable: true,
      },
      {
        key: "job_name",
        title: "Job",
        options: createFilterOptions(filterOptions?.job_names),
        searchable: true,
      },
      {
        key: "setup_name",
        title: "Setup",
        options: createFilterOptions(filterOptions?.setup_names),
        searchable: true,
      },
      {
        key: "recipes",
        title: "Recipe List",
        options: createFilterOptions(filterOptions?.recipes),
        searchable: true,
      },
      {
        key: "task_status",
        title: "Status",
        options: createStatusFilterOptions(),
        searchable: true,
      },
      ...(!isOnPremUser() || isUserManagementEnabled
        ? [
            {
              key: "created_by",
              title: "Operated by",
              options: createFilterOptions(filterOptions?.created_by),
              searchable: true,
            },
          ]
        : []),
    ];

    return baseFilters;
  }, [filterOptions, isUserManagementEnabled]);

  const queryParams = React.useMemo(() => {
    const params: Record<string, any> = {};

    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        if (key === "operationTime") {
          const dateRange = convertTimePeriodToDateRange(value);
          Object.assign(params, dateRange);
        } else {
          params[key] = value.length === 1 ? value[0] : value;
        }
      } else if (typeof value === "string" && value !== "") {
        // Handle single-value params like sort_by and sort_order
        params[key] = value;
      }
    });

    return params as GetTasksApiV1TasksGetQueryParams;
  }, [filters]);

  return { filters, setFilters, queryParams, availableFilters };
}
